import { memo, useState } from 'react';
import { useRouter } from 'next/router';

import { Checkbox, FieldSet, Label, LabeledControl, Legend, Toast, useToast } from '@zillow/constellation-10';

import { DatarouterError } from '@zg-rentals/rental-manager-env';
import {
  useRentalManagerUserPropertyEditWebApiHandler_hasBuildingWideWireless,
  useRentalManagerUserPropertyEditWebApiHandler_hasCableTv,
  useRentalManagerUserPropertyEditWebApiHandler_hasHighSpeedInternet,
  useRentalManagerUserPropertyEditWebApiHandler_hasSatelliteTv,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { useBatchMutationToast } from '../../../../hooks/useBatchMutationToast';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';
import { getIsInputFormDisabled } from '../../utils';

import {
  COMMUNICATION_KEYS,
  COMMUNICATION_METADATA,
  type CommunicationKey,
} from '../../../../constants/KeyFeaturesConstants';

import { useAuth } from '../../../AuthProvider';

type LocalState = Record<CommunicationKey, boolean>;

const CommunicationAmenities = (): JSX.Element | null => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const communicationTypes = listingDetails?.buildingAttributes?.communicationTypes;
  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const [localState, setLocalState] = useState<LocalState>({
    hasBuildingWideWireless: communicationTypes?.hasBuildingWideWireless ?? false,
    hasSatelliteTv: communicationTypes?.hasSatelliteTv ?? false,
    hasCableTv: communicationTypes?.hasCableTv ?? false,
    hasHighSpeedInternet: communicationTypes?.hasHighSpeedInternet ?? false,
  });

  const { startRequest, handleSuccess, handleError } = useBatchMutationToast({
    successMessage: 'Internet/satellite updated',
  });

  const isInputFormDisabled = getIsInputFormDisabled(listingDetails, canUpdateListing);

  const { mutateAsync: updateBuildingWideWireless } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasBuildingWideWireless,
    skipToast: true,
    meta: { context: { api: 'hasBuildingWideWireless', details: { listingAlias } } },
  });

  const { mutateAsync: updateSatelliteTv } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasSatelliteTv,
    skipToast: true,
    meta: { context: { api: 'hasSatelliteTv', details: { listingAlias } } },
  });

  const { mutateAsync: updateCableTv } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasCableTv,
    skipToast: true,
    meta: { context: { api: 'hasCableTv', details: { listingAlias } } },
  });

  const { mutateAsync: updateHighSpeedInternet } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasHighSpeedInternet,
    skipToast: true,
    meta: { context: { api: 'hasHighSpeedInternet', details: { listingAlias } } },
  });

  const mutationMap: Record<keyof LocalState, typeof updateBuildingWideWireless> = {
    hasBuildingWideWireless: updateBuildingWideWireless,
    hasSatelliteTv: updateSatelliteTv,
    hasCableTv: updateCableTv,
    hasHighSpeedInternet: updateHighSpeedInternet,
  };

  const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = event.target.name as CommunicationKey;
    const checked = event.target.value === 'true';
    const previousValue = !checked;

    startRequest();
    setLocalState((prev) => ({ ...prev, [fieldName]: checked }));

    try {
      const metadata = COMMUNICATION_METADATA[fieldName];
      await mutationMap[fieldName]({
        getParams: {
          [metadata.apiKey]: checked,
          listingAlias,
        },
      });
      handleSuccess();
    } catch (err) {
      handleError();
      setLocalState((prev) => ({ ...prev, [fieldName]: previousValue }));
      const errorMessage = DatarouterError.getDisplayMessage(
        err,
        'Something went wrong, refresh the page and try again',
      );
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  };

  return (
    <FieldSet legend={<Legend>Internet/satellite</Legend>} disabled={isInputFormDisabled}>
      {COMMUNICATION_KEYS.map((fieldName) => {
        const metadata = COMMUNICATION_METADATA[fieldName];
        return (
          <LabeledControl
            key={`communicationTypes-${metadata.label}`}
            label={<Label>{metadata.label}</Label>}
            control={
              <Checkbox
                name={fieldName}
                value={String(!localState[fieldName])}
                checked={localState[fieldName]}
                onChange={handleCheckboxChange}
              />
            }
          />
        );
      })}
    </FieldSet>
  );
};

export default memo(CommunicationAmenities);
